/**
 * DNA Social AI - Sistema de Conteúdo e Redes Sociais
 * Upload, Curadoria com IA, Geração de Legendas e Publicação
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import api from '../../lib/api';
import { 
  Camera, Upload, Image, Wand2, CheckCircle, XCircle, Clock, 
  Send, Calendar, Hash, Sparkles, Eye, Trash2, Edit, Filter,
  RefreshCw, Grid, List, Play, FileImage, Video, Layers,
  Instagram, Facebook, MessageSquare, Heart, Share2, BarChart3,
  Plus, Download, Copy, Star, AlertCircle, ThumbsUp, ThumbsDown
} from 'lucide-react';

const STATUS_CONFIG = {
  pendente: { label: 'Pendente', color: 'bg-gray-500', icon: Clock },
  em_curadoria: { label: 'Em Curadoria', color: 'bg-blue-500', icon: Wand2 },
  aprovado: { label: 'Aprovado', color: 'bg-green-500', icon: CheckCircle },
  agendado: { label: 'Agendado', color: 'bg-purple-500', icon: Calendar },
  publicado: { label: 'Publicado', color: 'bg-emerald-600', icon: Send },
  rejeitado: { label: 'Rejeitado', color: 'bg-red-500', icon: XCircle }
};

const TOM_VOZ_OPTIONS = [
  { value: 'acolhedor', label: 'Acolhedor', emoji: '💙' },
  { value: 'institucional', label: 'Institucional', emoji: '🏫' },
  { value: 'divertido', label: 'Divertido', emoji: '🎉' },
  { value: 'inspirador', label: 'Inspirador', emoji: '✨' }
];

const TIPO_POST_OPTIONS = [
  { value: 'feed', label: 'Feed', icon: Grid },
  { value: 'story', label: 'Story', icon: Layers },
  { value: 'reel', label: 'Reel', icon: Play },
  { value: 'carrossel', label: 'Carrossel', icon: FileImage }
];

export default function DnaSocialAI() {
  // Estados principais
  const [activeTab, setActiveTab] = useState('upload');
  const [conteudos, setConteudos] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [estatisticas, setEstatisticas] = useState(null);
  
  // Estados de upload
  const [arquivosSelecionados, setArquivosSelecionados] = useState([]);
  const [uploadForm, setUploadForm] = useState({
    titulo: '',
    descricao: '',
    turma: '',
    evento: '',
    tags: ''
  });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Estados de curadoria
  const [filtroStatus, setFiltroStatus] = useState('all');
  const [conteudoSelecionado, setConteudoSelecionado] = useState(null);
  const [showCuradoria, setShowCuradoria] = useState(false);
  const [executandoCuradoria, setExecutandoCuradoria] = useState(false);
  
  // Estados de geração de legenda
  const [showGerarLegenda, setShowGerarLegenda] = useState(false);
  const [legendaForm, setLegendaForm] = useState({
    descricao: '',
    tom: 'acolhedor',
    tipo_post: 'feed',
    incluir_hashtags: true,
    incluir_cta: true
  });
  const [legendasSugeridas, setLegendasSugeridas] = useState([]);
  const [gerandoLegenda, setGerandoLegenda] = useState(false);
  
  // Estados de criação de post
  const [showCriarPost, setShowCriarPost] = useState(false);
  const [novoPost, setNovoPost] = useState({
    content_ids: [],
    legenda: '',
    tipo: 'feed',
    redes: ['instagram'],
    hashtags: [],
    agendado_para: ''
  });
  
  // Carregar dados iniciais
  const carregarConteudos = useCallback(async () => {
    try {
      setLoading(true);
      const params = filtroStatus !== 'all' ? `?status=${filtroStatus}` : '';
      const res = await api.get(`/growth/social/conteudo${params}`);
      setConteudos(res.data.conteudos || []);
    } catch (error) {
      console.error('Erro ao carregar conteúdos:', error);
      toast.error('Erro ao carregar conteúdos');
    } finally {
      setLoading(false);
    }
  }, [filtroStatus]);
  
  const carregarPosts = useCallback(async () => {
    try {
      const res = await api.get('/growth/social/posts');
      setPosts(res.data.posts || []);
    } catch (error) {
      console.error('Erro ao carregar posts:', error);
    }
  }, []);
  
  const carregarEstatisticas = useCallback(async () => {
    try {
      const res = await api.get('/growth/social/analytics/resumo');
      setEstatisticas(res.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  }, []);
  
  useEffect(() => {
    carregarConteudos();
    carregarPosts();
    carregarEstatisticas();
  }, [carregarConteudos, carregarPosts, carregarEstatisticas]);
  
  // Handlers de upload
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => 
      file.type.startsWith('image/') || file.type.startsWith('video/')
    );
    
    if (validFiles.length < files.length) {
      toast.warning('Alguns arquivos foram ignorados (apenas imagens e vídeos são aceitos)');
    }
    
    setArquivosSelecionados(prev => [...prev, ...validFiles]);
  };
  
  const handleRemoveFile = (index) => {
    setArquivosSelecionados(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleUpload = async () => {
    if (arquivosSelecionados.length === 0) {
      toast.error('Selecione pelo menos um arquivo');
      return;
    }
    
    if (!uploadForm.titulo) {
      toast.error('Informe um título para o conteúdo');
      return;
    }
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('titulo', uploadForm.titulo);
      formData.append('descricao', uploadForm.descricao);
      formData.append('turma', uploadForm.turma);
      formData.append('evento', uploadForm.evento);
      formData.append('tags', uploadForm.tags);
      
      arquivosSelecionados.forEach(file => {
        formData.append('files', file);
      });
      
      const res = await api.post('/growth/social/conteudo/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`${res.data.conteudos.length} arquivo(s) enviado(s) com sucesso!`);
      
      // Limpar formulário
      setArquivosSelecionados([]);
      setUploadForm({ titulo: '', descricao: '', turma: '', evento: '', tags: '' });
      carregarConteudos();
      setActiveTab('curadoria');
    } catch (error) {
      console.error('Erro no upload:', error);
      toast.error('Erro ao enviar arquivos');
    } finally {
      setUploading(false);
    }
  };
  
  // Handlers de curadoria
  const handleExecutarCuradoria = async (conteudoId) => {
    try {
      setExecutandoCuradoria(true);
      const res = await api.post(`/growth/social/conteudo/${conteudoId}/curadoria`);
      toast.success('Curadoria realizada com sucesso!');
      
      // Atualizar conteúdo na lista
      setConteudos(prev => prev.map(c => 
        c.id === conteudoId 
          ? { ...c, curadoria: res.data.resultado, status: res.data.resultado.recomendacao === 'aprovado' ? 'aprovado' : 'em_curadoria' }
          : c
      ));
      
      // Se tiver um conteúdo selecionado, atualizar também
      if (conteudoSelecionado?.id === conteudoId) {
        setConteudoSelecionado(prev => ({
          ...prev,
          curadoria: res.data.resultado,
          status: res.data.resultado.recomendacao === 'aprovado' ? 'aprovado' : 'em_curadoria'
        }));
      }
    } catch (error) {
      console.error('Erro na curadoria:', error);
      toast.error('Erro ao executar curadoria');
    } finally {
      setExecutandoCuradoria(false);
    }
  };
  
  const handleAprovarConteudo = async (conteudoId) => {
    try {
      await api.post(`/growth/social/conteudo/${conteudoId}/aprovar`);
      toast.success('Conteúdo aprovado!');
      carregarConteudos();
      setShowCuradoria(false);
    } catch (error) {
      console.error('Erro ao aprovar:', error);
      toast.error('Erro ao aprovar conteúdo');
    }
  };
  
  const handleRejeitarConteudo = async (conteudoId, motivo = '') => {
    try {
      await api.post(`/growth/social/conteudo/${conteudoId}/rejeitar?motivo=${encodeURIComponent(motivo)}`);
      toast.success('Conteúdo rejeitado');
      carregarConteudos();
      setShowCuradoria(false);
    } catch (error) {
      console.error('Erro ao rejeitar:', error);
      toast.error('Erro ao rejeitar conteúdo');
    }
  };
  
  // Handler de geração de legenda
  const handleGerarLegenda = async () => {
    if (!legendaForm.descricao) {
      toast.error('Descreva o conteúdo para gerar a legenda');
      return;
    }
    
    try {
      setGerandoLegenda(true);
      const res = await api.post('/growth/social/gerar/legenda', legendaForm);
      setLegendasSugeridas(res.data.sugestoes || []);
      toast.success('Legendas geradas com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar legenda:', error);
      toast.error('Erro ao gerar legendas');
    } finally {
      setGerandoLegenda(false);
    }
  };
  
  const handleCopiarLegenda = (legenda) => {
    navigator.clipboard.writeText(legenda);
    toast.success('Legenda copiada!');
  };
  
  // Handler de criação de post
  const handleCriarPost = async () => {
    if (!novoPost.legenda) {
      toast.error('Informe uma legenda para o post');
      return;
    }
    
    try {
      await api.post('/growth/social/posts', novoPost);
      toast.success('Post criado com sucesso!');
      setShowCriarPost(false);
      setNovoPost({
        content_ids: [],
        legenda: '',
        tipo: 'feed',
        redes: ['instagram'],
        hashtags: [],
        agendado_para: ''
      });
      carregarPosts();
    } catch (error) {
      console.error('Erro ao criar post:', error);
      toast.error('Erro ao criar post');
    }
  };
  
  // Componente de card de conteúdo
  const ConteudoCard = ({ conteudo }) => {
    const statusConfig = STATUS_CONFIG[conteudo.status] || STATUS_CONFIG.pendente;
    const StatusIcon = statusConfig.icon;
    
    // Construir URL da imagem a partir do base64
    const getImageUrl = () => {
      if (conteudo.file_preview) {
        const mimeType = conteudo.content_type || 'image/jpeg';
        return `data:${mimeType};base64,${conteudo.file_preview}`;
      }
      return null;
    };
    
    const imageUrl = getImageUrl();
    
    return (
      <Card 
        className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
        onClick={() => {
          setConteudoSelecionado(conteudo);
          setShowCuradoria(true);
        }}
        data-testid={`conteudo-card-${conteudo.id}`}
      >
        <div className="relative aspect-square bg-gray-100 flex items-center justify-center overflow-hidden">
          {conteudo.tipo === 'video' ? (
            <Video className="w-12 h-12 text-gray-400" />
          ) : imageUrl ? (
            <img 
              src={imageUrl} 
              alt={conteudo.titulo}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : (
            <Image className="w-12 h-12 text-gray-400" />
          )}
          {/* Fallback icon when image fails */}
          <div className="hidden w-full h-full items-center justify-center">
            <Image className="w-12 h-12 text-gray-400" />
          </div>
          
          {/* Score de qualidade */}
          {conteudo.score_qualidade && (
            <div className="absolute top-2 right-2">
              <Badge 
                className={`${conteudo.score_qualidade >= 80 ? 'bg-green-500' : conteudo.score_qualidade >= 60 ? 'bg-yellow-500' : 'bg-red-500'} text-white`}
              >
                {conteudo.score_qualidade}%
              </Badge>
            </div>
          )}
          
          {/* Status badge */}
          <div className="absolute bottom-2 left-2">
            <Badge className={`${statusConfig.color} text-white flex items-center gap-1`}>
              <StatusIcon className="w-3 h-3" />
              {statusConfig.label}
            </Badge>
          </div>
        </div>
        
        <CardContent className="p-3">
          <h4 className="font-medium text-sm truncate">{conteudo.titulo}</h4>
          <p className="text-xs text-gray-500 mt-1">
            {conteudo.uploaded_by_name || 'Professor'}
          </p>
          {conteudo.turma && (
            <Badge variant="outline" className="mt-2 text-xs">
              {conteudo.turma}
            </Badge>
          )}
        </CardContent>
      </Card>
    );
  };
  
  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-purple-600" />
            DNA Social AI
          </h1>
          <p className="text-gray-500 text-sm">Upload, Curadoria com IA e Publicação de Conteúdo</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => setShowGerarLegenda(true)} data-testid="btn-gerar-legenda">
            <Wand2 className="w-4 h-4 mr-2" />
            Gerar Legenda
          </Button>
          
          <Button onClick={() => setShowCriarPost(true)} data-testid="btn-criar-post">
            <Plus className="w-4 h-4 mr-2" />
            Criar Post
          </Button>
        </div>
      </div>
      
      {/* Estatísticas */}
      {estatisticas && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-blue-100">
                  <FileImage className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Conteúdos</p>
                  <p className="text-xl font-bold">{estatisticas.conteudos_por_status?.pendente || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-green-100">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Aprovados</p>
                  <p className="text-xl font-bold">{estatisticas.conteudos_por_status?.aprovado || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-purple-100">
                  <Send className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Publicados</p>
                  <p className="text-xl font-bold">{estatisticas.posts?.total_publicados || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-pink-100">
                  <Heart className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Curtidas</p>
                  <p className="text-xl font-bold">{estatisticas.posts?.total_likes || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-orange-100">
                  <BarChart3 className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Engajamento</p>
                  <p className="text-xl font-bold">{estatisticas.engajamento_medio || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Tabs principais */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="upload" className="flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Upload
          </TabsTrigger>
          <TabsTrigger value="curadoria" className="flex items-center gap-2">
            <Wand2 className="w-4 h-4" />
            Curadoria
          </TabsTrigger>
          <TabsTrigger value="posts" className="flex items-center gap-2">
            <Grid className="w-4 h-4" />
            Posts
          </TabsTrigger>
          <TabsTrigger value="calendario" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Calendário
          </TabsTrigger>
        </TabsList>
        
        {/* Tab Upload */}
        <TabsContent value="upload">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-purple-600" />
                Enviar Fotos e Vídeos
              </CardTitle>
              <CardDescription>
                Professores podem enviar fotos e vídeos para curadoria. A IA irá analisar e sugerir as melhores opções.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Área de drop/seleção */}
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-400 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleFileSelect}
                  />
                  <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-2">Clique ou arraste arquivos aqui</p>
                  <p className="text-sm text-gray-400">Suporta imagens (JPG, PNG) e vídeos (MP4)</p>
                </div>
                
                {/* Arquivos selecionados */}
                {arquivosSelecionados.length > 0 && (
                  <div className="space-y-2">
                    <Label>Arquivos selecionados ({arquivosSelecionados.length})</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {arquivosSelecionados.map((file, index) => (
                        <div key={index} className="relative group bg-gray-100 rounded-lg p-2">
                          <div className="flex items-center gap-2">
                            {file.type.startsWith('video') ? (
                              <Video className="w-8 h-8 text-gray-400" />
                            ) : (
                              <FileImage className="w-8 h-8 text-gray-400" />
                            )}
                            <span className="text-xs truncate flex-1">{file.name}</span>
                          </div>
                          <button
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFile(index);
                            }}
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Formulário de metadados */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Título *</Label>
                    <Input 
                      placeholder="Ex: Aula de Ciências - 5º Ano"
                      value={uploadForm.titulo}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, titulo: e.target.value }))}
                    />
                  </div>
                  
                  <div>
                    <Label>Turma</Label>
                    <Input 
                      placeholder="Ex: 5º Ano A"
                      value={uploadForm.turma}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, turma: e.target.value }))}
                    />
                  </div>
                  
                  <div>
                    <Label>Evento</Label>
                    <Input 
                      placeholder="Ex: Feira de Ciências"
                      value={uploadForm.evento}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, evento: e.target.value }))}
                    />
                  </div>
                  
                  <div>
                    <Label>Tags (separadas por vírgula)</Label>
                    <Input 
                      placeholder="Ex: ciências, experimento, aprendizado"
                      value={uploadForm.tags}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, tags: e.target.value }))}
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <Label>Descrição</Label>
                    <Textarea 
                      placeholder="Descreva o momento capturado..."
                      value={uploadForm.descricao}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, descricao: e.target.value }))}
                      rows={3}
                    />
                  </div>
                </div>
                
                <Button 
                  className="w-full" 
                  onClick={handleUpload}
                  disabled={uploading || arquivosSelecionados.length === 0}
                  data-testid="btn-enviar-upload"
                >
                  {uploading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Enviar para Curadoria
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Tab Curadoria */}
        <TabsContent value="curadoria">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Wand2 className="w-5 h-5 text-purple-600" />
                    Curadoria de Conteúdo
                  </CardTitle>
                  <CardDescription>
                    Revise, aprove ou rejeite os conteúdos enviados pelos professores
                  </CardDescription>
                </div>
                
                <div className="flex items-center gap-2">
                  <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                    <SelectTrigger className="w-40">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Filtrar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Button variant="outline" size="icon" onClick={carregarConteudos}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
                </div>
              ) : conteudos.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileImage className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhum conteúdo encontrado</p>
                  <Button 
                    variant="link" 
                    className="mt-2"
                    onClick={() => setActiveTab('upload')}
                  >
                    Enviar primeiro conteúdo
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {conteudos.map(conteudo => (
                    <ConteudoCard key={conteudo.id} conteudo={conteudo} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Tab Posts */}
        <TabsContent value="posts">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Grid className="w-5 h-5 text-purple-600" />
                Posts Criados
              </CardTitle>
              <CardDescription>
                Gerencie seus posts agendados e publicados
              </CardDescription>
            </CardHeader>
            <CardContent>
              {posts.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Send className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhum post criado ainda</p>
                  <Button 
                    variant="link" 
                    className="mt-2"
                    onClick={() => setShowCriarPost(true)}
                  >
                    Criar primeiro post
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {posts.map(post => (
                    <div 
                      key={post.id}
                      className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex-1">
                        <p className="text-sm line-clamp-2">{post.legenda}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline">{post.tipo}</Badge>
                          {post.redes?.map(rede => (
                            <Badge key={rede} className="bg-purple-100 text-purple-700">
                              {rede === 'instagram' && <Instagram className="w-3 h-3 mr-1" />}
                              {rede === 'facebook' && <Facebook className="w-3 h-3 mr-1" />}
                              {rede}
                            </Badge>
                          ))}
                          <Badge className={STATUS_CONFIG[post.status]?.color || 'bg-gray-500'}>
                            {STATUS_CONFIG[post.status]?.label || post.status}
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Tab Calendário */}
        <TabsContent value="calendario">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                Calendário Editorial
              </CardTitle>
              <CardDescription>
                Planeje e visualize suas publicações ao longo do mês
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Calendário editorial em desenvolvimento</p>
                <p className="text-sm mt-2">Use a IA para gerar sugestões de conteúdo para o mês</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Modal de Curadoria */}
      <Dialog open={showCuradoria} onOpenChange={setShowCuradoria}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {conteudoSelecionado && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>{conteudoSelecionado.titulo}</span>
                  <Badge className={STATUS_CONFIG[conteudoSelecionado.status]?.color || 'bg-gray-500'}>
                    {STATUS_CONFIG[conteudoSelecionado.status]?.label || conteudoSelecionado.status}
                  </Badge>
                </DialogTitle>
                <DialogDescription>
                  Enviado por {conteudoSelecionado.uploaded_by_name || 'Professor'}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Preview do conteúdo */}
                <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                  {conteudoSelecionado.tipo === 'video' ? (
                    <Video className="w-16 h-16 text-gray-400" />
                  ) : (
                    <Image className="w-16 h-16 text-gray-400" />
                  )}
                </div>
                
                {/* Informações */}
                <div className="grid grid-cols-2 gap-4">
                  {conteudoSelecionado.turma && (
                    <div>
                      <Label className="text-gray-500">Turma</Label>
                      <p className="font-medium">{conteudoSelecionado.turma}</p>
                    </div>
                  )}
                  {conteudoSelecionado.evento && (
                    <div>
                      <Label className="text-gray-500">Evento</Label>
                      <p className="font-medium">{conteudoSelecionado.evento}</p>
                    </div>
                  )}
                </div>
                
                {conteudoSelecionado.descricao && (
                  <div>
                    <Label className="text-gray-500">Descrição</Label>
                    <p className="text-sm bg-gray-50 p-3 rounded-lg">{conteudoSelecionado.descricao}</p>
                  </div>
                )}
                
                {/* Resultado da curadoria */}
                {conteudoSelecionado.curadoria && (
                  <Card className="border-purple-200 bg-purple-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        Análise da IA
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Score de Qualidade</span>
                        <div className="flex items-center gap-2">
                          <Progress 
                            value={conteudoSelecionado.curadoria.score_qualidade} 
                            className="w-24 h-2"
                          />
                          <Badge className={
                            conteudoSelecionado.curadoria.score_qualidade >= 80 ? 'bg-green-500' :
                            conteudoSelecionado.curadoria.score_qualidade >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                          }>
                            {conteudoSelecionado.curadoria.score_qualidade}%
                          </Badge>
                        </div>
                      </div>
                      
                      <div>
                        <span className="text-sm text-gray-600">Recomendação: </span>
                        <Badge className={
                          conteudoSelecionado.curadoria.recomendacao === 'aprovado' ? 'bg-green-500' :
                          conteudoSelecionado.curadoria.recomendacao === 'rejeitar' ? 'bg-red-500' : 'bg-yellow-500'
                        }>
                          {conteudoSelecionado.curadoria.recomendacao}
                        </Badge>
                      </div>
                      
                      <p className="text-sm">{conteudoSelecionado.curadoria.motivo}</p>
                      
                      {conteudoSelecionado.curadoria.sugestoes?.length > 0 && (
                        <div>
                          <span className="text-sm text-gray-600">Sugestões:</span>
                          <ul className="list-disc list-inside text-sm mt-1">
                            {conteudoSelecionado.curadoria.sugestoes.map((s, i) => (
                              <li key={i}>{s}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {conteudoSelecionado.curadoria.legenda_sugerida && (
                        <div>
                          <span className="text-sm text-gray-600">Legenda sugerida:</span>
                          <p className="text-sm bg-white p-2 rounded mt-1 border">
                            {conteudoSelecionado.curadoria.legenda_sugerida}
                          </p>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="mt-1"
                            onClick={() => handleCopiarLegenda(conteudoSelecionado.curadoria.legenda_sugerida)}
                          >
                            <Copy className="w-3 h-3 mr-1" /> Copiar
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
              
              <DialogFooter className="flex-col sm:flex-row gap-2">
                {!conteudoSelecionado.curadoria && (
                  <Button 
                    variant="outline" 
                    onClick={() => handleExecutarCuradoria(conteudoSelecionado.id)}
                    disabled={executandoCuradoria}
                    className="w-full sm:w-auto"
                  >
                    {executandoCuradoria ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Wand2 className="w-4 h-4 mr-2" />
                    )}
                    Analisar com IA
                  </Button>
                )}
                
                <Button 
                  variant="destructive" 
                  onClick={() => handleRejeitarConteudo(conteudoSelecionado.id)}
                  className="w-full sm:w-auto"
                >
                  <ThumbsDown className="w-4 h-4 mr-2" />
                  Rejeitar
                </Button>
                
                <Button 
                  onClick={() => handleAprovarConteudo(conteudoSelecionado.id)}
                  className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
                >
                  <ThumbsUp className="w-4 h-4 mr-2" />
                  Aprovar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Modal Gerar Legenda */}
      <Dialog open={showGerarLegenda} onOpenChange={setShowGerarLegenda}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-purple-600" />
              Gerar Legenda com IA
            </DialogTitle>
            <DialogDescription>
              Descreva o conteúdo e a IA irá gerar sugestões de legendas criativas
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Descreva o conteúdo *</Label>
              <Textarea 
                placeholder="Ex: Alunos do 5º ano fazendo experimento de vulcão na aula de ciências"
                value={legendaForm.descricao}
                onChange={(e) => setLegendaForm(prev => ({ ...prev, descricao: e.target.value }))}
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tom de Voz</Label>
                <Select 
                  value={legendaForm.tom}
                  onValueChange={(v) => setLegendaForm(prev => ({ ...prev, tom: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TOM_VOZ_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.emoji} {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Tipo de Post</Label>
                <Select 
                  value={legendaForm.tipo_post}
                  onValueChange={(v) => setLegendaForm(prev => ({ ...prev, tipo_post: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPO_POST_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch 
                  checked={legendaForm.incluir_hashtags}
                  onCheckedChange={(v) => setLegendaForm(prev => ({ ...prev, incluir_hashtags: v }))}
                />
                <Label>Incluir hashtags</Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch 
                  checked={legendaForm.incluir_cta}
                  onCheckedChange={(v) => setLegendaForm(prev => ({ ...prev, incluir_cta: v }))}
                />
                <Label>Incluir call-to-action</Label>
              </div>
            </div>
            
            <Button 
              className="w-full"
              onClick={handleGerarLegenda}
              disabled={gerandoLegenda || !legendaForm.descricao}
            >
              {gerandoLegenda ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Gerando...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Gerar Legendas
                </>
              )}
            </Button>
            
            {/* Legendas sugeridas */}
            {legendasSugeridas.length > 0 && (
              <div className="space-y-3">
                <Label>Sugestões de Legenda</Label>
                {legendasSugeridas.map((sugestao, index) => (
                  <Card key={index} className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm flex-1 whitespace-pre-wrap">{sugestao.legenda}</p>
                      <div className="flex items-center gap-1">
                        <Badge variant="outline" className="text-xs">
                          {sugestao.score}%
                        </Badge>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleCopiarLegenda(sugestao.legenda)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal Criar Post */}
      <Dialog open={showCriarPost} onOpenChange={setShowCriarPost}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-purple-600" />
              Criar Novo Post
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Legenda *</Label>
              <Textarea 
                placeholder="Escreva a legenda do post..."
                value={novoPost.legenda}
                onChange={(e) => setNovoPost(prev => ({ ...prev, legenda: e.target.value }))}
                rows={4}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de Post</Label>
                <Select 
                  value={novoPost.tipo}
                  onValueChange={(v) => setNovoPost(prev => ({ ...prev, tipo: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPO_POST_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Agendar para</Label>
                <Input 
                  type="datetime-local"
                  value={novoPost.agendado_para}
                  onChange={(e) => setNovoPost(prev => ({ ...prev, agendado_para: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Redes Sociais</Label>
              <div className="flex gap-2 mt-2">
                <Button 
                  variant={novoPost.redes.includes('instagram') ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setNovoPost(prev => ({
                    ...prev,
                    redes: prev.redes.includes('instagram') 
                      ? prev.redes.filter(r => r !== 'instagram')
                      : [...prev.redes, 'instagram']
                  }))}
                >
                  <Instagram className="w-4 h-4 mr-1" />
                  Instagram
                </Button>
                <Button 
                  variant={novoPost.redes.includes('facebook') ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setNovoPost(prev => ({
                    ...prev,
                    redes: prev.redes.includes('facebook') 
                      ? prev.redes.filter(r => r !== 'facebook')
                      : [...prev.redes, 'facebook']
                  }))}
                >
                  <Facebook className="w-4 h-4 mr-1" />
                  Facebook
                </Button>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCriarPost(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCriarPost}>
              <Send className="w-4 h-4 mr-2" />
              Criar Post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
